import { meshPickTargetsAtom } from '@/atoms';
import { useSetAtom } from 'jotai';
import { ReactNode, useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * A <group> wrapper that registers itself with meshPickTargetsAtom
 * so measuring tools can raycast against it.
 *
 * `rotation` is an Euler XYZ tuple in radians — see
 * `eulerFromLayerSettings` for the per-layer mapping. When omitted the
 * group has identity rotation; the same applies to `position` (Three.js
 * default is the origin).
 */
const PickableGroup = ({
  children,
  position,
  rotation,
}: {
  children: ReactNode;
  position: THREE.Vector3;
  rotation?: [number, number, number];
}) => {
  const ref = useRef<THREE.Group>(null);
  const setTargets = useSetAtom(meshPickTargetsAtom);

  useEffect(() => {
    const group = ref.current;
    if (!group) return;
    setTargets((prev) => [...prev, group]);
    return () => setTargets((prev) => prev.filter((t) => t !== group));
  }, [setTargets]);

  return (
    <group ref={ref} position={position} rotation={rotation}>
      {children}
    </group>
  );
};

export default PickableGroup;
